import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DOBDatePicker } from '../ui/date-picker';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileCompletionModal = ({ open, user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);

  // Step 1 state
  const [formData, setFormData] = useState({
    indos_number: '',
    mobile_number: '+91',
    date_of_birth: ''
  });

  // Step 2 state - Security questions
  const [selectedQuestions, setSelectedQuestions] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' }
  ]);

  // Fetch security questions on mount
  useEffect(() => {
    if (open) {
      fetchSecurityQuestions();
    }
  }, [open]);

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setSecurityQuestions(response.data.questions);
    } catch (error) {
      toast.error('Failed to load security questions');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    // Validate INDoS format
    const indosPattern = /^[0-9]{2}[A-Za-z]{2}[0-9]{4}$/;
    if (!indosPattern.test(formData.indos_number)) {
      toast.error('Invalid INDoS number format. Expected: NNLLNNNN (2 digits, 2 letters, 4 digits)');
      return false;
    }

    // Validate mobile format
    const mobilePattern = /^\+91[6-9][0-9]{9}$/;
    if (!mobilePattern.test(formData.mobile_number)) {
      toast.error('Invalid mobile number. Expected: +91XXXXXXXXXX');
      return false;
    }

    if (!formData.date_of_birth) {
      toast.error('Please enter your date of birth');
      return false;
    }

    return true;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1()) return;

    toast.success('Step 1 completed! Now set up your security questions.');
    setStep(2);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...selectedQuestions];
    updated[index][field] = value;
    setSelectedQuestions(updated);
  };

  const validateStep2 = () => {
    // Check all questions are selected and unique
    const questions = selectedQuestions.map(q => q.question);
    const uniqueQuestions = new Set(questions);
    
    if (questions.some(q => !q)) {
      toast.error('Please select all 5 security questions');
      return false;
    }

    if (uniqueQuestions.size !== 5) {
      toast.error('Please select 5 different security questions');
      return false;
    }

    // Check all answers are filled
    if (selectedQuestions.some(q => !q.answer.trim())) {
      toast.error('Please answer all security questions');
      return false;
    }

    return true;
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        security_questions: selectedQuestions
      };
      
      const response = await axios.post(`${API}/auth/complete-profile`, payload);
      const { access_token, user: updatedUser } = response.data;
      
      // Update token
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Profile completed successfully!');
      onComplete(updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Profile completion failed');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableQuestions = (currentIndex) => {
    const selected = selectedQuestions
      .map((q, idx) => idx !== currentIndex ? q.question : null)
      .filter(Boolean);
    return securityQuestions.filter(q => !selected.includes(q));
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto overflow-x-hidden" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-[#0A7EA4]">
            Complete Your Profile
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-gray-600">
            {step === 1 ? 'Step 1 of 2: Basic Information' : 'Step 2 of 2: Security Questions'}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="text-blue-600 mt-0.5" size={18} />
            <p className="text-sm text-blue-800">
              To enhance security and enable password recovery, please complete your profile with INDoS number and security questions.
            </p>
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="indos_number">
                  INDoS Number * 
                  <span className="text-xs text-gray-500 ml-1">(8 characters)</span>
                </Label>
                <Input
                  id="indos_number"
                  type="text"
                  value={formData.indos_number}
                  onChange={(e) => handleInputChange('indos_number', e.target.value.toUpperCase())}
                  required
                  className="mt-1"
                  placeholder="Enter your INDoS"
                  maxLength={8}
                />
              </div>

              <div>
                <Label htmlFor="mobile_number">
                  Mobile Number * 
                  <span className="text-xs text-gray-500 ml-1">(+91XXXXXXXXXX)</span>
                </Label>
                <Input
                  id="mobile_number"
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                  required
                  className="mt-1"
                  placeholder="+919876543210"
                  maxLength={13}
                />
              </div>

              <div>
                <Label htmlFor="date_of_birth">Date of Birth *</Label>
                <div className="mt-1">
                  <DOBDatePicker
                    date={formData.date_of_birth ? new Date(formData.date_of_birth) : null}
                    onDateChange={(date) => handleInputChange('date_of_birth', date ? format(date, 'yyyy-MM-dd') : '')}
                    placeholder="Select your date of birth"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Your Account:</strong> {user?.email}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Name:</strong> {user?.name}
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
              disabled={loading}
            >
              Next: Security Questions
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleStep2Submit} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> Keep these answers secret and remember them. 
                  They will be used for password recovery.
                </p>
              </div>
            </div>

            {selectedQuestions.map((item, index) => (
              <div key={index} className="space-y-2 p-3 border rounded-lg bg-gray-50">
                <Label className="text-sm font-medium">Security Question {index + 1} *</Label>
                <Select
                  value={item.question}
                  onValueChange={(value) => handleQuestionChange(index, 'question', value)}
                >
                  <SelectTrigger className="bg-white w-full min-h-[40px] h-auto whitespace-normal text-left">
                    <SelectValue placeholder="Select a question" className="whitespace-normal" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[90vw] sm:max-w-[500px]">
                    {getAvailableQuestions(index).map((question) => (
                      <SelectItem key={question} value={question} className="text-xs whitespace-normal py-3 leading-relaxed cursor-pointer">
                        {question}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {item.question && (
                  <Input
                    type="text"
                    value={item.answer}
                    onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                    className="bg-white w-full"
                    placeholder="Your answer"
                  />
                )}
              </div>
            ))}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                disabled={loading}
              >
                {loading ? 'Completing Profile...' : 'Complete Profile'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
